import { Web3 } from 'web3';
import { IBlockchain, SupportedNetworks } from './blockchain';
import { TOKEN_ABI } from './token-abi';
import { ContractAddress } from './BEP20Contracts';
import Big from 'big.js';

declare const window: any;


export class EthereumCompatibleBlockchain implements IBlockchain {
  web3: Web3;
  walletAddress!: string;

  constructor() {
    this.web3 = new Web3(window.ethereum);
  }


  async connect(): Promise<void> {
    try {
      const accounts = await this.web3.eth.requestAccounts();
      this.walletAddress = accounts[0];
      if (!this.walletAddress) {
        throw new Error('Wallet address is required');
      }
      console.log('Connected to the network.');
    } catch (error) {
      console.error('Failed to connect:', error);
      throw error;
    }
  }

  async sendTransaction(toAddress: string, amount: string): Promise<string> {
    const tx = {
      from: this.walletAddress,
      to: toAddress,
      value: this.web3.utils.toWei(amount, 'ether'),
      gas: 21000
    };
    const transaction = await this.web3.eth.sendTransaction(tx);
    return transaction.transactionHash.toString();
  }

  async sendToken(toAddress: string, tokenId: string, amount: string, network: SupportedNetworks): Promise<string> {
    try {
      const contractInformation = ContractAddress[network][tokenId];
      if (!contractInformation) {
        throw new Error('Contract address is not defined');
      }
      const tokenContract = new this.web3.eth.Contract(TOKEN_ABI, contractInformation['contractId']);
      const data = await tokenContract.methods.transfer(toAddress, Big(amount).mul(Big(10).pow(contractInformation['tokenDecimal'])).toNumber(), { from: this.walletAddress }).send({ from: this.walletAddress });
      return data.transactionHash;
    } catch (error) {
      throw error;
    }
  }

  isNetworkSupported(networkId: string): boolean {
    switch (networkId) {
      case 'ARBITRUM':
      case 'POLYGON':
      case 'BEP20':
      case 'ERC20':
      case 'OPTIMISM':
      case 'AvalancheC':
      case 'FTM':
        return true;
      default:
        return false;
    }
  }

  getTransactionLink(network: SupportedNetworks, tx: string) {
    switch (network) {
      case 'BEP20':
        return `https://bscscan.com/tx/${tx}`;
      case 'ARBITRUM':
        return `https://arbiscan.io/tx/${tx}`;
      case 'POLYGON':
        return `https://polygonscan.com/tx/${tx}`;
      case 'ERC20':
        return `https://etherscan.io/tx/${tx}`;
      case 'OPTIMISM':
        return `https://optimistic.etherscan.io/tx/${tx}`;
      case 'FTM':
        return `https://ftmscan.com/tx/${tx}`;
      case 'AvalancheC':
        return `https://snowtrace.io/tx/${tx}`;
      default:
        return tx;

    }
  }

  async getBalanceToken(id: string, network: SupportedNetworks) {
    const contractInformation = ContractAddress[network][id];
    if (!contractInformation) {
      throw new Error('Contract address is not defined');
    }
    const tokenContract = new this.web3.eth.Contract(TOKEN_ABI, contractInformation['contractId']);
    const balance = await tokenContract.methods.balanceOf(this.walletAddress).call() as string;
    return Big(balance).div(Big(10).pow(contractInformation['tokenDecimal'])).toNumber();

  }

  async getBalanceNativeToken() {
    return this.web3.utils.fromWei((await this.web3.eth.getBalance(this.walletAddress)).toString(), 'ether');
  }

  async selectCorrectNetwork(network: SupportedNetworks) {
    switch (network) {
      case 'ERC20':
        return await window.ethereum.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: '0x1' }], // Chain ID for Ethereum Mainnet in hexadecimal
        });
      case 'BEP20':
        return await window.ethereum.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: '0x38' }], // Chain ID for Binance Smart Chain Mainnet in hexadecimal
        });
      case 'FTM':
        return await window.ethereum.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: '0xfa' }], // Chain ID for Fantom Mainnet in hexadecimal
        });
      case 'OPTIMISM':
        return await window.ethereum.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: '0xa' }], // Chain ID for Optimism in hexadecimal
        });
      case 'POLYGON':
        return await window.ethereum.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: '0x89' }], // Chain ID for Polygon Mainnet in hexadecimal
        });
      case 'ARBITRUM':
        return await window.ethereum.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: '0xa4c1' }], // Chain ID for Arbitrum in hexadecimal
        });
      case 'AvalancheC':
        return await window.ethereum.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: '0xa86a' }], // Chain ID for Avalanche C-Chain in hexadecimal
        });
    }

  }


  checkIsNativeToken(id: string, network: SupportedNetworks): boolean {
    switch (network) {
      case 'POLYGON':
        return id === 'POL';
      case 'BEP20':
        return id === 'BNB';
      case 'ERC20':
        return id === 'ETH';
      case 'AvalancheC':
        return id === 'AVAX';
      case 'FTM':
        return id === 'FTM';
      default:
        return false;
    }
  }

}

